/**
 * What the app keeps about Sync (issue #25, ADR-0017): the signed-in Account and Device in the
 * settings, and one bookkeeping row per record in the `sync` store.
 *
 * Types only, and no imports but the wire format, so `src/character/store.ts` can name the
 * session in its Settings without depending on the engine.
 */
import type { SyncCollection, SyncDocument, SyncErrorCode } from "../../shared/sync-format";

/** The Account a Device is signed in to, and the Device Token it carries. One settings row. */
export interface SyncSession {
  /** Opaque, non-expiring, sent as `Authorization: Bearer`; rotated at every sign-in. */
  token: string;
  account: { id: string; issuer: string; email: string | null; name: string | null };
  device: { id: string; name: string };
  /** The pull cursor the Account's change sequence gave out; "" before the first pull. */
  cursor: string;
  /** Device id to Device name, as the Account last reported them, for naming a Conflict Copy. */
  deviceNames: Record<string, string>;
  signedInAt: number;
  /** When the last sync finished without an error; null before the first. */
  syncedAt: number | null;
  /**
   * Why syncing has stopped until the person acts: the Device was removed from the Account, or
   * the Account is at a ceiling. Cleared by signing in again or by a sync asked for by hand.
   */
  blocked: { code: SyncErrorCode; message: string } | null;
}

/** Why an incoming record was stored unapplied rather than written over the local one. */
export type ParkReason =
  /** A Session is open on this Character here; the pull never touches one (docs/design.md). */
  | { kind: "session" }
  /** It was saved under a Snapshot this Device does not have yet; it waits for the update. */
  | { kind: "snapshot"; tag: string }
  /** It was saved by a newer build of the app than this one; it waits for the update. */
  | { kind: "app"; schema: number };

/**
 * One record's sync bookkeeping. Rows exist only while the Device is signed in, except for the
 * Conflict Copies and the Recently Deleted mirror, which outlive a sign-out.
 *
 * There is no dirty flag: a record whose `updatedAt` is not `syncedAt` has changed since the
 * Account last saw it. Derived rather than stored, so a crash between a save and its
 * bookkeeping cannot lose a change.
 */
export interface SyncRecord {
  /** `<collection>:<id>`. */
  key: string;
  collection: SyncCollection;
  id: string;
  /** The version the Account holds, as this Device last saw it; null when it holds none. */
  version: number | null;
  /** The local record's `updatedAt` in milliseconds when it was last pushed or pulled. */
  syncedAt: number | null;
  /**
   * Whether the local record is this Device's own work rather than one that arrived from
   * another. A Session travels with a Character, so this is what tells a Session open here —
   * which the pull must never disturb — from one that came in with someone else's record.
   */
  wroteHere: boolean;
  /**
   * The Device the local version came from, when it arrived from the Account rather than being
   * written here (`wroteHere` false). Only "Use this copy" reads it, to name the version it puts
   * aside as the Conflict Copy in turn (#26).
   */
  from?: string | null;
  /**
   * The record was deleted: pushed as a tombstone and kept here so Recently Deleted can undo it
   * for thirty days. `pushed` is false while the tombstone is still only local, and `device` is
   * the id of the Device that deleted it, which Recently Deleted names (#26); null on a row
   * written before the id was recorded, or by a Device the Account never named.
   */
  deleted: { at: number; pushed: boolean; data: unknown; device?: string | null } | null;
  /** An incoming record that could not be applied yet, kept whole until it can be. */
  parked: { document: SyncDocument; reason: ParkReason; at: number } | null;
  /**
   * This record is a Conflict Copy: the id of the Character that kept the Account, the name it
   * had, and the Device whose version lost. Conflict Copies are never pushed.
   */
  conflict: { of: string; name: string; device: string; at: number } | null;
}

export function trackingKey(collection: SyncCollection, id: string): string {
  return `${collection}:${id}`;
}

/** A fresh row: nothing known about the record yet, which reads as never synced. */
export function emptyRecord(collection: SyncCollection, id: string): SyncRecord {
  return { key: trackingKey(collection, id), collection, id, version: null, syncedAt: null, wroteHere: true, deleted: null, parked: null, conflict: null };
}
